import React, { useMemo, useEffect } from "react";

import { css, cx } from "@emotion/css";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { ChipSelect } from "@/uiKit/atoms/ChipSelect.tsx";
import { SelectCategory } from "@/uiKit/atoms/inputs/SelectCategory.tsx";
import { SelectItem } from "@/uiKit/atoms/inputs/SelectItem.tsx";
import { Tooltip } from "@/uiKit/atoms/Tooltip.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { FormSelectInput } from "@/uiKit/molecules/form/FormSelectInput.tsx";

type DiabetesType = "type1" | "type2" | "gestational";
type InsulinScheme = "noInsulin" | "insulinPen" | "insulinPump";

type InclusionCriteria =
  | "adult_monitoring_d1"
  | "adult_discovery_d1"
  | "teen_monitoring_d1"
  | "teen_discovery_d1"
  | "corticotherapie"
  | "teen_care_gap"
  | "start_basal_d2"
  | "monitoring_d2"
  | "pregnate_d"
  | "gestational_insulined"
  | "pump_monitoring"
  | "pump_initiation"
  | "pump_initiation_hybrid_loop";
type InclusionTier = "base" | "level1" | "level2";

type InclusionCriteriaMap = Record<DiabetesType, Record<InsulinScheme, InclusionCriteria[]>>;
type InclusionTierMap = Record<InclusionCriteria, InclusionTier>;

type InclusionCriteriaByTier = Record<InclusionTier | "all", InclusionCriteria[]>;

export type InclusionCriteriaFormFieldProps = {
  diabetesType: DiabetesType;
  insulinScheme: InsulinScheme | undefined;
  criteriaMap: InclusionCriteriaMap;
  tierMap: InclusionTierMap;
  criteriaTranslationMapping: (criteria: InclusionCriteria) => string;
  tierTranslationMapping: (tier: InclusionTier) => string;
  tierDescriptionTranslationMapping: (tier: InclusionTier) => string;
  name?: string;
  className?: string;
};

export const InclusionCriteriaFormField: React.FC<InclusionCriteriaFormFieldProps> = ({
  name = "inclusionCriteria",
  diabetesType,
  insulinScheme,
  criteriaMap,
  tierMap,
  criteriaTranslationMapping,
  tierTranslationMapping,
  tierDescriptionTranslationMapping,
  className,
}) => {
  const { t } = useTranslation();

  const styles = useStyles(makeStyles);
  const context = useFormContext();
  const inclusionCriteria: InclusionCriteria = useWatch({
    name,
    control: context.control,
  });
  const tier = inclusionCriteria ? tierMap[inclusionCriteria] : undefined;

  const criteriaChoice = useMemo((): InclusionCriteriaByTier => {
    if (insulinScheme && diabetesType) {
      const criteriaChoiceByDiabetesType = criteriaMap[diabetesType];
      const criterion =
        insulinScheme in criteriaChoiceByDiabetesType ? criteriaChoiceByDiabetesType[insulinScheme] : [];
      return {
        base: criterion.filter(criterion => tierMap[criterion] === "base"),
        level1: criterion.filter(criterion => tierMap[criterion] === "level1"),
        level2: criterion.filter(criterion => tierMap[criterion] === "level2"),
        all: criterion,
      };
    }
    return {
      base: [],
      level1: [],
      level2: [],
      all: [],
    };
  }, [insulinScheme, diabetesType, criteriaMap, tierMap]);

  useEffect(() => {
    if (criteriaChoice.all.length === 1) {
      context.setValue("inclusionCriteria", criteriaChoice.all[0]);
    }
  }, [criteriaChoice, context.setValue]);

  useEffect(() => {
    if (inclusionCriteria) {
      context.setValue("inclusionTier", tierMap[inclusionCriteria], {
        shouldTouch: true,
        shouldDirty: true,
      });
    } else {
      context.resetField("inclusionTier");
    }
  }, [inclusionCriteria, tierMap, context]);

  return (
    <div id="inclusion-criteria-form-field" className={cx(styles.root, className)}>
      <FormSelectInput
        id="inclusion-criteria-form-field-select"
        className={styles.select}
        name={name}
        label={t("diabetesForms.criteria")}
        fullWidth
        reserveErrorSpace
        disabled={!criteriaChoice || !diabetesType || !insulinScheme}
        onClick={() => {
          if (!diabetesType) {
            context.setValue("diabetesType", diabetesType, {
              shouldTouch: true,
              shouldDirty: true,
              shouldValidate: true,
            });
          }
          if (!insulinScheme) {
            context.setValue("insulinScheme", insulinScheme, {
              shouldTouch: true,
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }}
      >
        {criteriaChoice.base.length > 0 && <SelectCategory>{t("diabetesForms.criteriaBaseHeader")}</SelectCategory>}
        {criteriaChoice.base.map((criteria: InclusionCriteria) => (
          <SelectItem key={criteria} value={criteria}>
            {t(criteriaTranslationMapping(criteria))}
          </SelectItem>
        ))}
        {criteriaChoice.level1.length > 0 && <SelectCategory>{t("diabetesForms.criteriaLevel1Header")}</SelectCategory>}
        {criteriaChoice.level1.map((criteria: InclusionCriteria) => (
          <SelectItem key={criteria} value={criteria}>
            {t(criteriaTranslationMapping(criteria))}
          </SelectItem>
        ))}
        {criteriaChoice.level2.length > 0 && <SelectCategory>{t("diabetesForms.criteriaLevel2Header")}</SelectCategory>}
        {criteriaChoice.level2.map((criteria: InclusionCriteria) => (
          <SelectItem key={criteria} value={criteria}>
            {t(criteriaTranslationMapping(criteria))}
          </SelectItem>
        ))}
      </FormSelectInput>
      {!!tier && (
        <Tooltip
          id="inclusion-criteria-tier-tooltip"
          content={<Typography>{t(tierDescriptionTranslationMapping(tier))}</Typography>}
          placement="top"
        >
          <ChipSelect id="tls-tier-chips" selected={false} label={t(tierTranslationMapping(tier))} />
        </Tooltip>
      )}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(10),
    width: "100%",
    alignItems: "flex-start",
  }),
  select: css({
    flex: "1 1",
  }),
});
